import { useCallback, useEffect, useRef, useState } from 'react'
import { AutoComplete, Button, Drawer, Input, Tag, message } from 'antd'
import { CloseOutlined, SendOutlined, SwapOutlined } from '@ant-design/icons'
import { buscarProcessosIa, generateProcessBriefing, getAiStatus, getPainelProcesso, sendAiMessage } from 'services/ai'
import { getUserCachedData } from 'utils/UserAuthorization'
import { getErrorMessage } from 'utils/errorMessage'
import Markdown from './Markdown'
import CartaoProcesso from './CartaoProcesso'
import * as S from './styles'

const SUGESTOES_GERAIS = [
  'Quais prazos vencem esta semana?',
  'Resuma as últimas intimações do escritório',
  'Como contar prazo em dias úteis no CPC?',
  'Qual o prazo de contrarrazões de apelação?',
]
const SUGESTOES_PROCESSO = [
  'Resuma este processo em 5 linhas',
  'Qual o próximo passo?',
  'Há prazo correndo? Até quando?',
  'Explique a última decisão para o cliente',
]

// Resumo gerado → texto para o chat
function resumoEmTexto(b) {
  if (!b) return 'Não consegui gerar o resumo agora.'
  const passos = (b.proximosPassos || []).map((p) => `- **${p.acao}**${p.prazo ? ` (até ${p.prazo})` : ''}`).join('\n')
  const atencao = (b.pontosDeAtencao || []).map((p) => `- ${p}`).join('\n')
  return [
    `**Resumo**\n${b.resumo || ''}`,
    b.situacao ? `**Situação atual**\n${b.situacao}` : null,
    passos ? `**Próximos passos**\n${passos}` : null,
    atencao ? `**Pontos de atenção**\n${atencao}` : null,
  ].filter(Boolean).join('\n\n')
}

// Painel lateral da Sofia: pergunta sobre qualquer processo (com cartão animado
// de andamentos, intimações e prazos) ou dúvidas jurídicas gerais.
export default function SofiaChat({ open, onClose, processoInicial = null }) {
  const usuario = getUserCachedData()
  const [processo, setProcesso] = useState(processoInicial)
  const [painel, setPainel] = useState(null)
  const [mensagens, setMensagens] = useState([])
  const [texto, setTexto] = useState('')
  const [pensando, setPensando] = useState(false)
  const [resumindo, setResumindo] = useState(false)
  const [opcoes, setOpcoes] = useState([])
  const [busca, setBusca] = useState('')
  const [modelo, setModelo] = useState(null)
  const fim = useRef(null)

  useEffect(() => { if (open) getAiStatus().then((s) => setModelo(s?.modelo || null)) }, [open])
  useEffect(() => { if (processoInicial) setProcesso(processoInicial) }, [processoInicial])
  useEffect(() => { fim.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' }) }, [mensagens, pensando, painel])

  // trocou de processo: carrega o cartão e começa conversa nova
  useEffect(() => {
    setMensagens([])
    setPainel(null)
    if (!processo?.id) return
    getPainelProcesso(processo.id).then(setPainel).catch(() => setPainel(null))
  }, [processo?.id])

  const procurar = useCallback(async (q) => {
    setBusca(q)
    if (String(q).trim().length < 2) { setOpcoes([]); return }
    const lista = await buscarProcessosIa(q).catch(() => [])
    setOpcoes(lista.map((p) => ({
      value: p.id,
      processo: { id: p.id, NProcesso: p.numero },
      label: (
        <div>
          <div style={{ fontWeight: 600 }}>{p.numero}</div>
          <div style={{ fontSize: 12, color: 'var(--yrm-text-3)' }}>{[p.cliente, p.partes].filter(Boolean).join(' · ')}</div>
        </div>
      ),
    })))
  }, [])

  const enviar = async (pergunta) => {
    const t = String(pergunta ?? texto).trim()
    if (!t || pensando) return
    const historico = mensagens.map((m) => ({ role: m.role, text: m.text }))
    setMensagens((m) => [...m, { role: 'user', text: t }])
    setTexto('')
    setPensando(true)
    try {
      const r = await sendAiMessage(t, historico, { mode: processo?.id ? 'process' : 'help', processId: processo?.id })
      setMensagens((m) => [...m, { role: 'model', text: r.text, processos: r.processosCitados || [] }])
    } catch (error) {
      message.error(getErrorMessage(error, 'A Sofia não conseguiu responder agora.'))
      setMensagens((m) => m.slice(0, -1))
      setTexto(t)
    } finally {
      setPensando(false)
    }
  }

  const resumir = async () => {
    if (!processo?.id) return
    setResumindo(true)
    setMensagens((m) => [...m, { role: 'user', text: 'Resuma o processo e os próximos passos.' }])
    try {
      const r = await generateProcessBriefing(processo.id)
      setMensagens((m) => [...m, { role: 'model', text: resumoEmTexto(r?.briefing) }])
    } catch (error) {
      message.error(getErrorMessage(error, 'Não foi possível gerar o resumo.'))
    } finally {
      setResumindo(false)
    }
  }

  const sugestoes = processo?.id ? SUGESTOES_PROCESSO : SUGESTOES_GERAIS
  const primeiroNome = String(usuario?.first_name || '').split(' ')[0]

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={480}
      closable={false}
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
      destroyOnHidden={false}
    >
      <S.Cabecalho>
        <S.Avatar aria-hidden>✨</S.Avatar>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Sofia</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>Assistente jurídica{modelo ? ` · ${modelo}` : ''}</div>
        </div>
        <Button type="text" aria-label="Fechar" icon={<CloseOutlined style={{ color: '#fff' }} />} onClick={onClose} />
      </S.Cabecalho>

      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--yrm-border)', background: 'var(--yrm-surface)' }}>
        {processo?.id ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag color="blue" style={{ margin: 0 }}>Processo</Tag>
            <strong style={{ fontSize: 13 }}>{processo.NProcesso || processo.numero}</strong>
            <Button size="small" type="link" icon={<SwapOutlined />} onClick={() => setProcesso(null)}>trocar</Button>
          </div>
        ) : (
          <AutoComplete
            style={{ width: '100%' }}
            value={busca}
            options={opcoes}
            onSearch={procurar}
            onSelect={(_, o) => { setProcesso(o.processo); setBusca('') }}
            placeholder="Perguntar sobre um processo… (número, parte ou cliente)"
            aria-label="Escolher processo"
          />
        )}
      </div>

      <S.Corpo>
        {processo?.id && <CartaoProcesso painel={painel} onResumir={resumir} resumindo={resumindo} />}

        {!mensagens.length && (
          <>
            {!processo?.id && (
              <S.Balao>
                Olá{primeiroNome ? `, ${primeiroNome}` : ''}! Posso responder dúvidas jurídicas, olhar os prazos e intimações do
                escritório ou analisar um processo — é só escolher acima.
              </S.Balao>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {sugestoes.map((s, i) => <S.Chip key={s} $i={i} type="button" onClick={() => enviar(s)}>{s}</S.Chip>)}
            </div>
          </>
        )}

        {mensagens.map((m, i) => (
          <S.Balao key={i} $meu={m.role === 'user'}>
            {m.role === 'user' ? m.text : <Markdown texto={m.text} />}
            {m.processos?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {m.processos.map((p) => (
                  <Button key={p.id} size="small" onClick={() => setProcesso({ id: p.id, NProcesso: p.numero })}>Analisar {p.numero}</Button>
                ))}
              </div>
            )}
          </S.Balao>
        ))}
        {(pensando || resumindo) && <S.Digitando aria-label="Sofia está escrevendo"><span /><span /><span /></S.Digitando>}
        <div ref={fim} />
      </S.Corpo>

      <S.Rodape>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <Input.TextArea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); enviar() } }}
            autoSize={{ minRows: 1, maxRows: 5 }}
            placeholder={processo?.id ? 'Pergunte sobre este processo…' : 'Pergunte qualquer coisa jurídica…'}
            aria-label="Mensagem para a Sofia"
          />
          <Button type="primary" shape="circle" icon={<SendOutlined />} aria-label="Enviar" disabled={!texto.trim() || pensando} onClick={() => enviar()} />
        </div>
        <div style={{ fontSize: 12, color: 'var(--yrm-text-4)', marginTop: 6 }}>A Sofia pode errar: confira prazos e citações antes de usar.</div>
      </S.Rodape>
    </Drawer>
  )
}
